import {
  BasicButton,
  ButtonGroup,
  DateSelectField,
  Divider,
  Grid,
  GridItem,
  LegacyModal,
  Link,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PrimaryButton,
  Text,
  TextInputField,
  Toggle,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { date, object, string } from "yup";

import {
  AddressInput,
  initialValues as initialAddressValues,
  addressSchema,
} from "../../../../components/AddressInput";
import { FormikErrorFocus } from "../../../../components/FormikErrorFocus";

type EmploymentHistoryModalProps = {
  index: number;
  employer?: components["schemas"]["EmploymentCheckFieldValue"];
  onSave: (
    employer: components["schemas"]["EmploymentCheckFieldValue"],
    index: number
  ) => void;
  onDelete?: () => void;
  trigger: JSX.Element;
  defaultOpen?: boolean;
};

export function EmploymentHistoryModal({
  employer,
  onSave,
  onDelete,
  index,
  trigger,
  defaultOpen = false,
}: EmploymentHistoryModalProps) {
  const { t } = useTranslation();

  const [isCurrentEmployer, setIsCurrentEmployer] = useState<boolean>(
    !!employer && !employer.end_date
  );

  const schema = object({
    company_name: string().required().label(t("Company name")),
    position: string().trim().required().label(t("Position")),
    employment_url: string().trim().label(t("Website URL")),
    accomplishments: string()
      .trim()
      .label(t("Accomplishments / responsibilities")),
    start_date: date().required().label(t("Start date")),
    end_date: date().when(["start_date"], ([start_date], schema) => {
      if (isCurrentEmployer) {
        return schema.notRequired();
      }

      if (start_date) {
        return schema
          .required()
          .label(t("End date"))
          .min(start_date, t("End date must be after start date"));
      }

      return schema.required().label(t("End date"));
    }),
    address: addressSchema,
  });

  return (
    <ModalTrigger defaultOpen={defaultOpen} trigger={() => trigger}>
      {(close) => (
        <Formik
          initialValues={
            employer ?? {
              company_name: "",
              position: "",
              start_date: null,
              end_date: null,
              employment_url: "",
              accomplishments: "",
              address: initialAddressValues,
              can_contact: true,
            }
          }
          onSubmit={(employer) => {
            onSave(
              employer as components["schemas"]["EmploymentCheckFieldValue"],
              index
            );
            close();
          }}
          validationSchema={schema}
        >
          {({ values, handleSubmit, setFieldValue }) => {
            return (
              <LegacyModal width="456px">
                <ModalHeader
                  close={close}
                  title={employer ? t("Edit employer") : t("Add employer")}
                  testID="employment-history-add-modal"
                />
                <ModalContent>
                  <Form>
                    <Grid form>
                      <GridItem>
                        <Text style="summary/content-primary">
                          <Trans>Employer details</Trans>
                        </Text>
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="company_name"
                          label={t("Company")}
                          as={TextInputField}
                          testID="employment-history-modal-company-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="employment_url"
                          label={t("Website URL")}
                          as={TextInputField}
                          testID="employment-history-modal-url-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="position"
                          label={t("Position")}
                          as={TextInputField}
                          testID="employment-history-modal-position-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          isRequired
                          name="accomplishments"
                          label={t("Accomplishments / responsibilities")}
                          as={TextInputField}
                          testID="employment-history-modal-accomplishments-responsibilities-input"
                        />
                      </GridItem>

                      <GridItem>
                        <Field
                          label={t("Start date")}
                          name="start_date"
                          isRequired
                          value={values.start_date}
                          as={DateSelectField}
                          testID="employment-history-modal-start-date-select"
                        />
                      </GridItem>

                      <GridItem
                        alignItems="left"
                        className={css({
                          flexDirection: "row",
                        })}
                      >
                        <Toggle
                          isSelected={isCurrentEmployer}
                          onChange={(value) => {
                            setFieldValue("end_date", null);
                            setIsCurrentEmployer(value);
                          }}
                          testID="employment-history-modal-currently-working"
                        >
                          {t("I am currently working at this company")}
                        </Toggle>
                      </GridItem>

                      {!isCurrentEmployer && (
                        <GridItem>
                          <Field
                            label={t("End date")}
                            name="end_date"
                            isRequired
                            value={values.end_date}
                            as={DateSelectField}
                            testID="employment-history-modal-end-date-select"
                          />
                        </GridItem>
                      )}
                      <GridItem>
                        <Divider />
                      </GridItem>

                      <GridItem>
                        <Text style="summary/content-primary">
                          <Trans>Employer address</Trans>
                        </Text>
                      </GridItem>

                      <AddressInput nestedFieldName="address" />
                      <GridItem>
                        <Toggle
                          isSelected={!values.can_contact}
                          onChange={(value) => {
                            setFieldValue("can_contact", !value);
                          }}
                          testID="employment-history-modal-do-not-contact"
                        >
                          {t("Do not contact this reference")}
                        </Toggle>
                      </GridItem>
                    </Grid>

                    <FormikErrorFocus />
                  </Form>
                </ModalContent>
                <ModalFooter
                  className={css({
                    justifyContent: onDelete ? "space-between" : "flex-end",
                  })}
                >
                  {onDelete && (
                    <Link
                      textStyle="global/body-secondary"
                      textColor="text-weak"
                      onPress={() => {
                        onDelete();
                        close();
                      }}
                      testID="employment-history-modal-delete-button"
                    >
                      <Trans>Delete employer</Trans>
                    </Link>
                  )}
                  <ButtonGroup>
                    <BasicButton
                      size="medium"
                      onPress={close}
                      testID="employment-history-modal-cancel-button"
                    >
                      <Trans>Cancel</Trans>
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      onPress={() => handleSubmit()}
                      testID="employment-history-modal-save-button"
                    >
                      <Trans>Save employer</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </ModalFooter>
              </LegacyModal>
            );
          }}
        </Formik>
      )}
    </ModalTrigger>
  );
}
